import { useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useSnapshot } from 'valtio';
import { DateTime } from 'luxon';
import {
  CleaningLevel,
  ServiceSiteKind,
  ServiceType,
  VehicleClass,
} from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import {
  Card,
  CardBody,
  ErrorText,
  Field,
  FormGrid,
  Input,
  Muted,
  Select,
  Stack,
} from '../../components/ui';
import { useBackendPlaces } from '../../hooks/useBackendPlaces';
import { useWindowWidth } from '../../hooks/useMobileScreenListenerHook';
import { usePlacePredictions, type PlacePrediction } from '../../hooks/usePlacePredictions';
import { useMyServiceSitesQuery } from '../../services/serviceService/__generated__/queries.generated';
import { authStore } from '../../../store/authStore';
import {
  CLEANING_LEVEL_LABELS,
  SERVICE_TYPE_LABELS,
  SERVICE_TYPES,
  VEHICLE_CLASS_LABELS,
  type CleaningLevelKey,
  type VehicleClassKey,
} from '../ServiceProviderPage/priceTables';
import { CheckRow } from '../ServiceProviderPage/serviceControls';
import { servicesForSite } from './serviceJob';
import {
  hasPlace,
  MAX_HANDOVER_HOURS,
  timeLabel,
  WINDOW_TIMES,
  windowProblem,
  windowSummary,
  searchPostalCode,
  type ServiceSearchState,
} from './serviceSearchState';

const BATHROOMS = Array.from({ length: 16 }, (_, i) => (i + 1) / 2);

const Suggestions = styled.ul`
  ${tw`absolute left-0 right-0 z-20 p-1 m-0 mt-1 list-none`}
  top: 100%;
  background: var(--color-bg-elevated, #fff);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-menu);
`;

const Suggestion = styled.button`
  ${tw`w-full px-3 py-2 text-sm text-left cursor-pointer`}
  border: none;
  background: none;
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  &:hover {
    background: var(--color-bg-secondary);
  }
`;

const WhereWrap = styled.div`
  ${tw`relative`}
`;

const Sheet = styled.div`
  ${tw`fixed inset-0 z-50 p-4 overflow-y-auto`}
  background: var(--color-bg-elevated, #fff);
`;

const SheetTop = styled.div`
  ${tw`flex items-center justify-between mb-4`}
`;

const PillButton = styled.button`
  ${tw`flex flex-col w-full px-4 py-3 text-left cursor-pointer`}
  border: 1px solid var(--color-border);
  border-radius: 9999px;
  background: var(--color-bg-elevated, #fff);
  box-shadow: var(--shadow-card);
  color: var(--color-text-primary);
`;

const classKey = (c: VehicleClass) => c.toLowerCase() as VehicleClassKey;
const levelKey = (l: CleaningLevel) => l.toLowerCase() as CleaningLevelKey;

/**
 * The services search bar (spec 019 W10): Where (a ZIP code, an address, or
 * one of your own homes and vehicles once signed in) | Date | Window (two
 * 30-minute times) | Service with its size. Phones get one button and a
 * full-screen panel.
 */
export function ServiceSearchBar({
  value,
  onSearch,
  compact = false,
}: {
  value: ServiceSearchState;
  onSearch: (state: ServiceSearchState) => void;
  /** Results page: phones and desktops both start from the summary pill. */
  compact?: boolean;
}) {
  const { isMobile } = useWindowWidth();
  const [draft, setDraft] = useState<ServiceSearchState>(value);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useSnapshot(authStore);
  const sitesQuery = useMyServiceSitesQuery({
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });
  const sites = sitesQuery.data?.myServiceSites ?? [];
  const fallback = useBackendPlaces();
  const places = usePlacePredictions({ types: ['geocode'], fallback });

  useEffect(() => setDraft(value), [value]);

  const set = (patch: Partial<ServiceSearchState>) => {
    setDraft(previous => ({ ...previous, ...patch }));
    setError(null);
  };

  const typeWhere = (text: string) => {
    const zip = searchPostalCode(text) ?? undefined;
    set({ where: text, zip, lat: undefined, lng: undefined, bounds: undefined, siteId: undefined });
    if (!zip && text.trim().length > 2) places.search(text);
    else places.clear();
  };

  const pick = async (prediction: PlacePrediction) => {
    places.clear();
    const resolved = await places.resolve(prediction);
    if (!resolved) return;
    const zip = searchPostalCode(resolved.zipCode) ?? undefined;
    set({
      where: prediction.description,
      zip,
      lat: zip ? undefined : resolved.latitude,
      lng: zip ? undefined : resolved.longitude,
      bounds: undefined,
      siteId: undefined,
    });
  };

  const pickSite = (siteId: string) => {
    const site = sites.find(s => s.id === siteId);
    if (!site) {
      set({ siteId: undefined });
      return;
    }
    const types = servicesForSite(site.kind);
    set({
      siteId,
      where: site.nickname,
      zip: site.zip,
      lat: undefined,
      lng: undefined,
      bounds: undefined,
      serviceType: types.includes(draft.serviceType) ? draft.serviceType : types[0],
    });
  };

  const submit = async () => {
    let next = draft;
    if (!hasPlace(next) && places.predictions[0]) {
      const resolved = await places.resolve(places.predictions[0]);
      if (resolved) {
        const zip = searchPostalCode(resolved.zipCode) ?? undefined;
        next = {
          ...next,
          where: places.predictions[0].description,
          zip,
          lat: zip ? undefined : resolved.latitude,
          lng: zip ? undefined : resolved.longitude,
        };
      }
    }
    if (!hasPlace(next)) {
      setError('Enter a ZIP code or an address');
      return;
    }
    const problem = windowProblem(next);
    if (problem) {
      setError(problem);
      return;
    }
    places.clear();
    setOpen(false);
    onSearch(next);
  };

  const site = sites.find(s => s.id === draft.siteId);
  const allowedTypes = site ? servicesForSite(site.kind) : SERVICE_TYPES;
  const today = DateTime.local().toISODate()!;

  const fields = (
    <Stack $gap={4}>
      <FormGrid $cols={2}>
        {sites.length > 0 && (
          <Field label="Your home or vehicle" htmlFor="svc-site">
            <Select
              id="svc-site"
              value={draft.siteId ?? ''}
              onChange={e => pickSite(e.target.value)}
              data-testid="svc-site"
            >
              <option value="">Search by ZIP or address</option>
              {sites.map(s => (
                <option key={s.id} value={s.id}>
                  {s.nickname} ({s.kind === ServiceSiteKind.Home ? 'home' : 'vehicle'}, {s.zip})
                </option>
              ))}
            </Select>
          </Field>
        )}
        {!draft.siteId && (
          <Field label="Where" htmlFor="svc-where" hint="A ZIP code or an address">
            <WhereWrap>
              <Input
                id="svc-where"
                value={draft.where}
                placeholder="85004"
                autoComplete="off"
                onChange={e => typeWhere(e.target.value)}
                data-testid="svc-where"
              />
              {places.predictions.length > 0 && (
                <Suggestions>
                  {places.predictions.slice(0, 5).map(p => (
                    <li key={p.placeId}>
                      <Suggestion type="button" onClick={() => pick(p)}>
                        {p.description}
                      </Suggestion>
                    </li>
                  ))}
                </Suggestions>
              )}
            </WhereWrap>
          </Field>
        )}
      </FormGrid>
      <FormGrid $cols={3}>
        <Field label="Date" htmlFor="svc-date">
          <Input
            id="svc-date"
            type="date"
            min={today}
            value={draft.date}
            onChange={e => set({ date: e.target.value })}
            data-testid="svc-date"
          />
        </Field>
        <Field label="Window starts" htmlFor="svc-from">
          <Select
            id="svc-from"
            value={draft.windowStart}
            onChange={e => set({ windowStart: e.target.value })}
            data-testid="svc-from"
          >
            {WINDOW_TIMES.map(t => (
              <option key={t} value={t}>
                {timeLabel(t)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Window ends" htmlFor="svc-to">
          <Select
            id="svc-to"
            value={draft.windowEnd}
            onChange={e => set({ windowEnd: e.target.value })}
            data-testid="svc-to"
          >
            {WINDOW_TIMES.map(t => (
              <option key={t} value={t}>
                {timeLabel(t)}
              </option>
            ))}
          </Select>
        </Field>
      </FormGrid>
      <FormGrid $cols={3}>
        <Field label="Service" htmlFor="svc-type">
          <Select
            id="svc-type"
            value={draft.serviceType}
            onChange={e => set({ serviceType: e.target.value as ServiceType })}
            data-testid="svc-type"
          >
            {allowedTypes.map(type => (
              <option key={type} value={type}>
                {SERVICE_TYPE_LABELS[type]}
              </option>
            ))}
          </Select>
        </Field>
        {draft.serviceType === ServiceType.HomeTurnover && !draft.siteId && (
          <>
            <Field label="Bedrooms" htmlFor="svc-bd">
              <Select
                id="svc-bd"
                value={String(draft.bedrooms)}
                onChange={e => set({ bedrooms: Number(e.target.value) })}
              >
                {Array.from({ length: 11 }, (_, i) => (
                  <option key={i} value={i}>
                    {i === 0 ? 'Studio' : i}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Bathrooms" htmlFor="svc-ba">
              <Select
                id="svc-ba"
                value={String(draft.bathrooms)}
                onChange={e => set({ bathrooms: Number(e.target.value) })}
              >
                {BATHROOMS.map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}
        {draft.serviceType === ServiceType.VehicleCleaning && (
          <>
            {!draft.siteId && (
              <Field label="Vehicle" htmlFor="svc-class">
                <Select
                  id="svc-class"
                  value={draft.vehicleClass}
                  onChange={e => set({ vehicleClass: e.target.value as VehicleClass })}
                >
                  {Object.values(VehicleClass).map(c => (
                    <option key={c} value={c}>
                      {VEHICLE_CLASS_LABELS[classKey(c)]}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field label="Clean" htmlFor="svc-level">
              <Select
                id="svc-level"
                value={draft.cleaningLevel}
                onChange={e => set({ cleaningLevel: e.target.value as CleaningLevel })}
              >
                {Object.values(CleaningLevel).map(l => (
                  <option key={l} value={l}>
                    {CLEANING_LEVEL_LABELS[levelKey(l)]}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}
        {draft.serviceType === ServiceType.VehicleHandover && (
          <Field
            label="Up to"
            htmlFor="svc-hours"
            hint="Billed for the time it takes, at least 1 hour"
          >
            <Select
              id="svc-hours"
              value={String(draft.handoverHours)}
              onChange={e => set({ handoverHours: Number(e.target.value) })}
            >
              {Array.from({ length: MAX_HANDOVER_HOURS }, (_, i) => i + 1).map(h => (
                <option key={h} value={h}>
                  {h} {h === 1 ? 'hour' : 'hours'}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </FormGrid>
      {draft.serviceType === ServiceType.HomeTurnover && (
        <CheckRow
          label="Same-day turnover (guests leave and arrive the same day)"
          checked={draft.sameDayTurnover}
          onChange={sameDayTurnover => set({ sameDayTurnover })}
        />
      )}
      {error && <ErrorText role="alert">{error}</ErrorText>}
      <div>
        <Button type="button" text="Search" size="lg" onClick={submit} dataTestId="svc-search" />
      </div>
    </Stack>
  );

  const summary = (
    <PillButton type="button" onClick={() => setOpen(true)} data-testid="svc-search-pill">
      <strong>
        {value.where || 'Where?'} · {SERVICE_TYPE_LABELS[value.serviceType]}
      </strong>
      <Muted $size="sm">{windowSummary(value)}</Muted>
    </PillButton>
  );

  if (isMobile) {
    return (
      <>
        {summary}
        {open && (
          <Sheet role="dialog" aria-label="Search">
            <SheetTop>
              <strong>Find a worker</strong>
              <Button
                type="button"
                theme="text"
                size="sm"
                text="Close"
                onClick={() => setOpen(false)}
              />
            </SheetTop>
            {fields}
          </Sheet>
        )}
      </>
    );
  }
  if (compact && !open) return summary;
  return (
    <Card>
      <CardBody>{fields}</CardBody>
    </Card>
  );
}
